/**
 * URL helpers. We standardize on trailing-slash canonical URLs
 * (https://hari.co.id/path/) for sitemaps, canonicals, og:url and any
 * absolute URL we serialize. Internal <Link to="/path"> stays without
 * trailing slash so TanStack Router type-safe matching keeps working;
 * a server-side 308 redirect (see src/routes/__root.tsx) normalizes
 * the address bar.
 */

/** Append "/" to a path-only string (preserves query/hash). */
export function withSlash(input: string): string {
  if (!input) return "/";
  // Split off query/hash so we don't insert "/" in the middle.
  const qIdx = input.indexOf("?");
  const hIdx = input.indexOf("#");
  const cut = [qIdx, hIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const path = cut === undefined ? input : input.slice(0, cut);
  const tail = cut === undefined ? "" : input.slice(cut);
  if (path.endsWith("/")) return path + tail;
  // Treat extension-bearing paths as files (no slash), e.g. /sitemap.xml.
  const last = path.split("/").pop() ?? "";
  if (/\.[a-z0-9]{2,5}$/i.test(last)) return path + tail;
  return path + "/" + tail;
}

/** Build an absolute canonical URL with trailing slash. */
export function canonicalUrl(origin: string, path: string): string {
  return `${origin.replace(/\/+$/, "")}${withSlash(path.startsWith("/") ? path : "/" + path)}`;
}

/**
 * Promote a path or partial URL to an absolute, trailing-slashed URL using the
 * canonical site origin. Pass-through for already-absolute http(s) URLs.
 * Used for BreadcrumbList JSON-LD where Google requires absolute `item` URLs.
 */
export function absUrl(input: string): string {
  if (!input) return "https://hari.co.id/";
  if (/^https?:\/\//i.test(input)) return input;
  const path = input.startsWith("/") ? input : "/" + input;
  return "https://hari.co.id" + withSlash(path);
}
